import { html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { BaseElement } from "../utils/ui-components";
import { Api, User } from "../api";
import { wavemindIcon } from "../utils/icons";

@customElement("landing-page")
export class LandingPage extends BaseElement {
    @state()
    private loading = false;

    @state()
    private error?: string;

    @state()
    private email = "";

    @state()
    private password = "";

    @state()
    private user?: User;

    @state()
    private initialLoading = true;

    async connectedCallback() {
        super.connectedCallback();
        try {
            this.user = await Api.me();
        } catch (err) {
            // Not logged in, that's fine
        } finally {
            this.initialLoading = false;
        }
    }

    private async handleSubmit(e: Event) {
        e.preventDefault();
        this.loading = true;
        this.error = undefined;

        try {
            await Api.login(this.email, this.password);
            window.location.href = "/search";
        } catch (err) {
            this.error = "Invalid credentials";
        } finally {
            this.loading = false;
        }
    }

    private renderLoggedIn() {
        return html`
            <div class="bg-white py-6 sm:py-8 px-4 shadow-sm rounded-lg sm:px-10">
                <div class="text-center space-y-4">
                    <p class="text-lg font-medium text-gray-900">Welcome back, ${this.user?.name}!</p>
                    <p class="text-sm text-gray-600">Ready to explore media content with AI assistance?</p>
                    <button
                        @click=${() => (window.location.href = "/search")}
                        class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Start Researching
                    </button>
                </div>
            </div>
        `;
    }

    render() {
        return html`
            <div class="min-h-screen bg-gray-100 py-6 px-4 sm:py-12 sm:px-6 lg:px-8">
                <div class="max-w-md w-full mx-auto space-y-6 sm:space-y-8">
                    <!-- Logo and Title -->
                    <div class="text-center">
                        <div class="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-blue-600">
                            <i class="w-full h-full icon">${wavemindIcon}</i>
                        </div>
                        <h1 class="mt-3 sm:mt-4 text-3xl sm:text-4xl font-bold text-gray-900">WaveMind</h1>
                        <p class="mt-2 text-sm text-gray-600">Media Research Assistant</p>
                    </div>

                    ${this.initialLoading
                        ? html`<div class="text-center text-gray-600">Loading...</div>`
                        : this.user
                        ? this.renderLoggedIn()
                        : html`
                              <!-- Info Card -->
                              <div class="bg-white py-6 sm:py-8 px-4 shadow-sm rounded-lg sm:px-10">
                                  <div class="text-center mb-6 sm:mb-8 space-y-3 sm:space-y-4">
                                      <div
                                          class="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                      >
                                          Early Prototype
                                      </div>
                                      <p class="text-sm text-gray-600 leading-relaxed">
                                          An experimental AI-powered research tool for exploring and analyzing media content. Currently limited to
                                          public broadcast content from ORF TV, Ö1 Radio, and selected podcasts.
                                      </p>
                                      <p class="text-xs text-gray-500">
                                          Access is currently <a href="mailto:contact@mariozechner.at" class="text-blue-500">invitation-only</a> as we
                                          refine the platform.
                                      </p>
                                  </div>

                                  <!-- Login Form -->
                                  <form class="space-y-4 sm:space-y-6" @submit=${this.handleSubmit}>
                                      <div>
                                          <label for="email" class="block text-sm font-medium text-gray-700">Email</label>
                                          <input
                                              type="email"
                                              id="email"
                                              required
                                              .value=${this.email}
                                              @input=${(e: InputEvent) => (this.email = (e.target as HTMLInputElement).value)}
                                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm
                                                focus:border-blue-500 focus:ring-blue-500 sm:text-sm
                                                h-10 px-3 bg-gray-50"
                                          />
                                      </div>

                                      <div>
                                          <label for="password" class="block text-sm font-medium text-gray-700">Password</label>
                                          <input
                                              type="password"
                                              id="password"
                                              required
                                              .value=${this.password}
                                              @input=${(e: InputEvent) => (this.password = (e.target as HTMLInputElement).value)}
                                              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm
                                                focus:border-blue-500 focus:ring-blue-500 sm:text-sm
                                                h-10 px-3 bg-gray-50"
                                          />
                                      </div>

                                      ${this.error ? html` <div class="text-red-600 text-sm">${this.error}</div> ` : null}

                                      <button
                                          type="submit"
                                          ?disabled=${this.loading}
                                          class="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                                            disabled:bg-blue-400 disabled:cursor-not-allowed"
                                      >
                                          ${this.loading ? "Signing in..." : "Sign in"}
                                      </button>
                                  </form>
                              </div>
                          `}
                </div>
            </div>
        `;
    }
}
